import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "./config";
import { DEFAULT_ABOUT } from "./constants";
import InlineBanner from "./InlineBanner";

/**
 * Group members map — Skool-style member discovery.
 * Fetches `community-members-map` (privacy already masked server-side) and
 * renders each member as an avatar marker, with name search, region filters,
 * a "near me" toggle, and an admin distribution panel.
 */

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

// HTML avatar marker (image or colored initial); admins get a colored ring.
function avatarIcon(m, primary) {
  const initial = escapeHtml((m.name || "?").charAt(0).toUpperCase());
  const inner = m.img
    ? `<img src="${escapeHtml(m.img)}" alt="" style="width:100%;height:100%;object-fit:cover" onerror="this.style.display='none'"/>`
    : `<span style="color:#fff;font-weight:700;font-size:14px">${initial}</span>`;
  const ring = m.is_admin ? primary : "#ffffff";
  return L.divIcon({
    className: "ff-avatar-pin",
    html:
      `<div style="width:38px;height:38px;border-radius:50%;overflow:hidden;border:3px solid ${ring};` +
      `background:${primary};display:flex;align-items:center;justify-content:center;` +
      `box-shadow:0 2px 6px rgba(0,0,0,.35)">${inner}</div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -20],
  });
}

// Fit the map to the visible markers (or recenter on a focused member).
function FitTo({ points, focus }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 80);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (focus) {
      map.setView([focus.lat, focus.lng], Math.max(map.getZoom(), 9), { animate: true });
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 9, { animate: false });
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, focus, map]);
  return null;
}

function SmallAvatar({ name, img, primary, size = 44, ring }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="rounded-full overflow-hidden flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, backgroundColor: primary, fontSize: size * 0.4, boxShadow: ring ? `0 0 0 2px ${ring}` : undefined }}
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : initial}
    </span>
  );
}

// Consistent custom dropdown chevron (native select arrows render differently
// per browser/OS and crowd the rounded-pill edge). Applied to every filter
// select so all four arrows sit in the same spot.
const CHEVRON_STYLE = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%236b7280' stroke-width='1.75'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.75rem center",
  backgroundSize: "1rem",
};

function Select({ value, onChange, options, placeholder, primary, className = "" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`appearance-none rounded-full border border-gray-200 bg-white pl-3.5 pr-9 py-2 text-sm text-gray-700 outline-none shadow-sm focus:border-gray-300 ${className}`}
      style={{ ...CHEVRON_STYLE, ...(value ? { borderColor: primary, color: primary } : {}) }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function MapView({ token, groupToken, primary = "#0274F7", member, groupCard, pointsCard }) {
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState(null);
  const [q, setQ] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");
  const [fRole, setFRole] = useState("");
  const [near, setNear] = useState(false);
  const [radius] = useState(100);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/community-members-map`, {
        community_token: token,
        group_token: groupToken,
        member_token: member?.auth_token,
      })
      .then((res) => {
        if (active && res.data?.error === 0) setResp(res.data);
        else if (active) setResp({ members: [], facets: {} });
      })
      .catch(() => active && setResp({ members: [], facets: {} }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken, member]);

  const all = useMemo(() => resp?.members || [], [resp]);
  const facets = resp?.facets || { countries: [], states: [], cities: [] };
  const me = resp?.me || null;
  const isAdmin = !!resp?.is_admin;
  const distribution = resp?.distribution || null;

  // All filtering is client-side over the privacy-masked set returned once.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = all.filter((m) => {
      if (needle && !(m.name || "").toLowerCase().includes(needle)) return false;
      if (fCountry && m.country !== fCountry) return false;
      if (fState && m.state !== fState) return false;
      if (fCity && m.city !== fCity) return false;
      if (fRole === "admin" && !m.is_admin) return false;
      if (fRole === "member" && m.is_admin) return false;
      if (near && (m.distance_km == null || m.distance_km > radius)) return false;
      return true;
    });
    if (near) list = [...list].sort((a, b) => (a.distance_km ?? 1e9) - (b.distance_km ?? 1e9));
    return list;
  }, [all, q, fCountry, fState, fCity, fRole, near, radius]);

  const hasMyLocation = !!me;

  const openDetail = (m) => setDetail(m);

  const resetFilters = () => {
    setQ(""); setFCountry(""); setFState(""); setFCity(""); setFRole(""); setNear(false);
  };

  const activeFilters = q || fCountry || fState || fCity || fRole || near;

  const mapBody = (
    <div className="space-y-5">
      {/* Premium, map-focused card: header strip + big full-width map. */}
      <div className="rounded-3xl border border-gray-200 shadow-sm overflow-hidden bg-white">
        {/* Header strip — light theme tinted with the community colour, matching
            the other member-page section headers. */}
        <div
          className="p-4 sm:p-5"
          style={{
            background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
            borderBottom: `1px solid ${primary}1f`,
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border bg-white shadow-sm"
                style={{ color: primary, borderColor: `${primary}29` }}
              >
                <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </span>
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight text-gray-900 leading-tight">Member Map</h2>
                <p className="text-[13px] text-gray-500 mt-0.5 truncate">Tap any avatar to view a member's profile</p>
              </div>
            </div>
            <span className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap" style={{ backgroundColor: `${primary}14`, color: primary }}>
              {loading ? "…" : `${filtered.length} ${filtered.length === 1 ? "member" : "members"}`}
            </span>
          </div>

          {/* Search + filters */}
          <div className="mt-4 space-y-2.5">
            {/* Search — full width on every screen */}
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search members by name…"
                className="w-full rounded-full border border-gray-200 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-800 outline-none shadow-sm focus:border-gray-300"
              />
            </div>

            {/* Filters — even 2-col grid on mobile, inline on sm+ */}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
              <Select value={fCountry} onChange={setFCountry} options={facets.countries || []} placeholder="All countries" primary={primary} className="w-full sm:w-auto" />
              <Select value={fState} onChange={setFState} options={facets.states || []} placeholder="All states" primary={primary} className="w-full sm:w-auto" />
              <Select value={fCity} onChange={setFCity} options={facets.cities || []} placeholder="All cities" primary={primary} className="w-full sm:w-auto" />
              <select
                value={fRole}
                onChange={(e) => setFRole(e.target.value)}
                className="w-full sm:w-auto appearance-none rounded-full border border-gray-200 bg-white pl-3.5 pr-9 py-2 text-sm text-gray-700 outline-none shadow-sm focus:border-gray-300"
                style={{ ...CHEVRON_STYLE, ...(fRole ? { borderColor: primary, color: primary } : {}) }}
              >
                <option value="">All roles</option>
                <option value="admin">Admins</option>
                <option value="member">Members</option>
              </select>

              <button
                type="button"
                onClick={() => hasMyLocation && setNear((v) => !v)}
                disabled={!hasMyLocation}
                title={hasMyLocation ? "" : "Set your location in your profile to use this"}
                className="col-span-2 sm:col-auto inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold border shadow-sm transition disabled:opacity-50"
                style={{
                  borderColor: near ? primary : "#e5e7eb",
                  backgroundColor: near ? primary : "#fff",
                  color: near ? "#fff" : "#374151",
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 11a3 3 0 100-6 3 3 0 000 6z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s7-7.5 7-13a7 7 0 10-14 0c0 5.5 7 13 7 13z" />
                </svg>
                Near me
              </button>

              {activeFilters && (
                <button type="button" onClick={resetFilters} className="col-span-2 sm:col-auto text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 sm:ml-0.5">
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Big map — isolate into its own stacking context so Leaflet's
            internal z-indexes (panes/controls up to 1000) can't paint over the
            header dropdown menus, which open above this whole section. */}
        <div className="relative z-0 isolate">
          {loading ? (
            <div className="h-[560px] max-md:h-[400px] flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
          ) : (
            <div style={{ height: 560 }} className="max-md:h-[400px] relative">
              <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom attributionControl={false} className="h-full w-full" style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitTo points={filtered} focus={selected} />
                {filtered.map((m) => (
                  <Marker
                    key={m.community_member_id}
                    position={[m.lat, m.lng]}
                    icon={avatarIcon(m, primary)}
                    eventHandlers={{ click: () => setSelected(m) }}
                  >
                    <Popup maxWidth={250} minWidth={200}>
                      <MarkerCard m={m} primary={primary} onView={() => openDetail(m)} />
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              {/* Branded overlay — replaces the default Leaflet/OSM attribution
                  badge at the bottom-right. Uses the app favicon as the icon. */}
              <div className="absolute bottom-3 right-3 z-[1000] flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 shadow-md ring-1 ring-black/5">
                <img
                  src={`${process.env.PUBLIC_URL}/favicon.ico`}
                  alt=""
                  className="w-4 h-4 rounded-sm object-contain"
                />
                <span className="text-[11px] font-semibold text-gray-700 leading-none">Flexicommunity Map</span>
              </div>

              {/* Empty state overlay */}
              {filtered.length === 0 && (
                <div className="absolute inset-0 z-[900] flex items-center justify-center bg-white/55 backdrop-blur-[1px] p-6">
                  <div className="text-center bg-white rounded-2xl shadow-xl ring-1 ring-black/5 px-6 py-5 max-w-xs">
                    <span className="mx-auto mb-3 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${primary}14`, color: primary }}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                        <circle cx="12" cy="11" r="2.5" />
                      </svg>
                    </span>
                    <p className="text-sm text-gray-600 font-medium">
                      {all.length === 0 ? "No members have shared a location yet." : "No members match these filters."}
                    </p>
                    {activeFilters && all.length > 0 && (
                      <button type="button" onClick={resetFilters} className="mt-3 text-sm font-semibold underline underline-offset-2" style={{ color: primary }}>
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin insights (region distribution) */}
      {isAdmin && distribution && (
        <DistributionPanel distribution={distribution} primary={primary} total={all.length} />
      )}

      {detail && <MemberDetailModal m={detail} primary={primary} onClose={() => setDetail(null)} />}
    </div>
  );

  // With a group card (passed from the group page) the map sits in the left
  // column and the group banner + My Points take the right rail, matching the
  // other pages.
  return (groupCard || pointsCard) ? (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
        {groupCard}
        {pointsCard}
        <InlineBanner token={token} groupToken={groupToken} placement="map_page" wrapperClass="space-y-3" />
      </aside>
      <div className="lg:col-span-8 order-1">{mapBody}</div>
    </div>
  ) : mapBody;
}

function MarkerCard({ m, primary, onView }) {
  return (
    <div className="min-w-[180px]">
      <div className="flex items-center gap-2.5">
        <SmallAvatar name={m.name} img={m.img} primary={primary} size={40} ring={m.is_admin ? primary : undefined} />
        <div className="min-w-0">
          <p className="font-bold text-gray-800 text-sm leading-tight truncate" style={{ margin: 0 }}>{m.name}</p>
          {m.location && <p className="text-[11px] text-gray-400 truncate" style={{ margin: 0 }}>{m.location}</p>}
        </div>
      </div>
      <p className="text-xs text-gray-600 mt-2 line-clamp-3" style={{ margin: "8px 0 0" }}>{m.about || DEFAULT_ABOUT}</p>
      {m.distance_km != null && <p className="text-[11px] text-gray-400 mt-1" style={{ margin: "4px 0 0" }}>{m.distance_km} km away</p>}
      <button
        type="button"
        onClick={onView}
        className="mt-2.5 w-full text-white text-xs font-semibold py-1.5 rounded-lg hover:opacity-90"
        style={{ backgroundColor: primary }}
      >
        View Profile
      </button>
    </div>
  );
}

function MemberDetailModal({ m, primary, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}>
          <button onClick={onClose} aria-label="Close" className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white text-lg leading-none transition">
            ×
          </button>
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white shadow-md overflow-hidden flex items-center justify-center text-2xl font-bold" style={{ color: primary }}>
            {m.img ? <img src={m.img} alt="" className="w-full h-full object-cover" /> : (m.name || "?").charAt(0).toUpperCase()}
          </div>
          <h3 className="text-white text-xl font-bold mt-4 flex items-center justify-center gap-2">
            {m.name}
            {m.is_admin && <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-white/20">Admin</span>}
          </h3>
          {m.location && <p className="text-white/85 text-sm mt-1">{m.location}</p>}
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-gray-50 rounded-xl py-3">
              <div className="font-bold text-xl" style={{ color: primary }}>{m.points ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">Points</div>
            </div>
            <div className="bg-gray-50 rounded-xl py-3">
              <div className="font-bold text-sm text-gray-900">{m.distance_km != null ? `${m.distance_km} km` : "—"}</div>
              <div className="text-xs text-gray-500 mt-0.5">Distance</div>
            </div>
          </div>

          <div>
            <span className="text-[13px] font-semibold text-gray-600 mb-1 block">About</span>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{m.about || DEFAULT_ABOUT}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DistributionPanel({ distribution, primary, total }) {
  const byCountry = Object.entries(distribution.by_country || {});
  const max = byCountry.reduce((mx, [, c]) => Math.max(mx, c), 0) || 1;
  return (
    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${primary}14`, color: primary }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 16l4-4 3 3 5-6" />
            </svg>
          </span>
          <div>
            <h4 className="font-bold text-gray-800 text-sm leading-tight">Member Distribution</h4>
            <p className="text-xs text-gray-400">Where your members are · admin view</p>
          </div>
        </div>
        <span className="shrink-0 text-xs font-semibold rounded-full px-2.5 py-1" style={{ background: `${primary}14`, color: primary }}>
          {total} on map
        </span>
      </div>
      {byCountry.length === 0 ? (
        <p className="text-xs text-gray-400">No location data yet.</p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {byCountry.slice(0, 10).map(([country, count]) => (
            <li key={country}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-700 font-medium truncate">{country}</span>
                <span className="text-gray-400">{count}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(count / max) * 100}%`, background: `linear-gradient(90deg, ${primary} 0%, ${primary}aa 100%)` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
